// RevenueFilter.js (Updated)

import React, { useState, useEffect } from "react"; // Make sure useEffect is imported
import styles from "./.FilterBoxComponents.module.css";
import revenueStyles from "./CompanyRevenueFilter.module.css";
import { formatWithCommas } from "functions/fields"; // Assuming this utility exists

const RevenueFilter = ({ revenue, setRevenue }) => {
  // Helper function to derive local state format from the revenue prop
  const getLocalStateFromProp = (propValue) => {
    const validProp = Array.isArray(propValue) ? propValue : [null, null];
    return validProp.map(e => (e === null ? "" : String(e)));
  };

  const [localRevenue, setLocalRevenue] = useState(() => getLocalStateFromProp(revenue));

  // Effect to synchronize local state when the revenue prop changes from the parent
  useEffect(() => {
    const updatedLocalRevenue = getLocalStateFromProp(revenue);
    setLocalRevenue((prev) => {
      const currentLocal = Array.isArray(prev) ? prev : ["", ""];
      if (updatedLocalRevenue[0] === currentLocal[0] && updatedLocalRevenue[1] === currentLocal[1]) {
        return prev;
      }
      return updatedLocalRevenue;
    });
  }, [revenue]);

  const handleChange = (event, index) => {
    const raw = event.target.value.replace(/,/g, "");
    const sanitized = raw.replace(/[^\d]/g, ""); // Keep only digits
    // Ensure localRevenue is treated as an array even if it becomes undefined momentarily
    const currentLocal = Array.isArray(localRevenue) ? localRevenue : ["", ""];
    const updated = [...currentLocal];
    updated[index] = sanitized;
    setLocalRevenue(updated);
  };

  const handleBlur = () => {
    // Ensure localRevenue is treated as an array
    const currentLocal = Array.isArray(localRevenue) ? localRevenue : ["", ""];
    let [minStr, maxStr] = currentLocal;

    // Default to empty string if undefined/null before checking emptiness
    minStr = minStr ?? "";
    maxStr = maxStr ?? "";

    const min = minStr === "" ? null : Number(minStr);
    const max = maxStr === "" ? null : Number(maxStr);

    // Ensure no NaN makes it through
    const updatedMin = isNaN(min) ? null : min;
    const updatedMax = isNaN(max) ? null : max;

    let finalMin = updatedMin;
    let finalMax = updatedMax;

    if (updatedMin !== null && updatedMax !== null && updatedMax < updatedMin) {
        // If max < min, set max = min to ensure validity
        finalMax = updatedMin;
    }

    // --- START CHANGE ---
    // Determine the correct state format for the parent
    let nextParentState;
    if (finalMin === null && finalMax === null) {
        nextParentState = []; // Use empty array if both are null/cleared
    } else {
        nextParentState = [finalMin, finalMax];
    }

    // Get current parent state, default to [] if not an array for comparison
    const currentParentState = Array.isArray(revenue) ? revenue : [];

    // Check if the state actually needs to change by comparing length and elements
    let needsUpdate = false;
    if (nextParentState.length !== currentParentState.length) {
        needsUpdate = true;
    } else if (nextParentState.length === 2) {
        // Only compare elements if both arrays have length 2
        if (nextParentState[0] !== currentParentState[0] || nextParentState[1] !== currentParentState[1]) {
            needsUpdate = true;
        }
    }
    // If both lengths are 0, needsUpdate remains false (no change needed)

    // Update parent state only if the calculated state is different
    if (needsUpdate) {
        setRevenue(nextParentState);
    }
    // --- END CHANGE ---


    // Update local state based on the final validated/adjusted values, ensuring strings
    const nextLocalState = [
        finalMin === null ? "" : String(finalMin),
        finalMax === null ? "" : String(finalMax)
    ];
    // Only update local state if it differs, prevents re-render if blur didn't change effective value
    if(nextLocalState[0] !== currentLocal[0] || nextLocalState[1] !== currentLocal[1]) {
       setLocalRevenue(nextLocalState);
    }
  };


  // Ensure localRevenue is always an array for rendering
  const displayRevenue = Array.isArray(localRevenue) ? localRevenue : ["", ""];

  return (
    <div className={styles.accordionContent}>
      <label>Company revenue is between:</label>
      <div className={revenueStyles.revenueRange}>
        <div className={revenueStyles.revenueInputContainer}>
          <label className={revenueStyles.minLabel}>Min</label>
          <label className={revenueStyles.dollarSign}>$</label>
          <input
            type="text"
            // Use displayRevenue which is guaranteed to be an array
            value={displayRevenue[0] === "" ? "" : formatWithCommas(displayRevenue[0])}
            onChange={(e) => handleChange(e, 0)}
            onBlur={handleBlur}
            placeholder="No min"
          />
        </div>
        <div className={revenueStyles.revenueInputContainer}>
          <label className={revenueStyles.maxLabel}>Max</label>
          <label className={revenueStyles.dollarSign}>$</label>
          <input
            type="text"
            // Use displayRevenue which is guaranteed to be an array
            value={displayRevenue[1] === "" ? "" : formatWithCommas(displayRevenue[1])}
            onChange={(e) => handleChange(e, 1)}
            onBlur={handleBlur}
            placeholder="No max"
           />
        </div>
      </div>
    </div>
  );
};

export default RevenueFilter;